// CUSTOM: personalised-debug.js

/**
 * Shared Observability Controller — personalised bundle system.
 *
 * Purpose:
 *   Provides a single, centralised debug/logging/timing/tracing layer for all
 *   personalised-* files. Replaces scattered, ungated console.log calls with a
 *   structured controller that is SILENT IN PRODUCTION by default.
 *
 * Usage:
 *   var dbg = window.personalised.debug;
 *   dbg.debug('My message', { foo: 'bar' });   // only in debug mode
 *   dbg.info('Info message');                  // only in debug mode
 *   dbg.warn('Warning');                       // always visible
 *   dbg.error('Error', err);                   // always visible
 *   dbg.time('label');                         // starts named timer (debug mode only)
 *   dbg.timeEnd('label');                      // ends timer and logs duration (debug mode only)
 *   dbg.trace('eventName', { key: 'value' });  // structured flow event (debug mode only)
 *
 * Activation (debug mode OFF by default):
 *   URL param:       ?personalised_debug=true
 *   sessionStorage:  sessionStorage.setItem('personalised_debug_mode', 'true')
 *   Once activated via URL param it persists for the browser session.
 *
 * Output prefix:
 *   All messages are prefixed [personalised] for easy console filtering.
 *
 * Tapcart / WebView safe:
 *   Uses only standard browser APIs: console.*, sessionStorage, URLSearchParams,
 *   Date.now(). All sessionStorage access is wrapped in try/catch.
 *
 * Load order:
 *   This file must be loaded before any other personalised-* script.
 *   It is included in layout/theme.liquid immediately before personalised-mode.js.
 */

(function () {
  // Do not overwrite if already initialised (e.g. double-include guard).
  if (window.personalised && window.personalised.debug) return;

  // Ensure namespace exists.
  window.personalised = window.personalised || {};

  var PREFIX = '[personalised]';
  var SESSION_KEY = 'personalised_debug_mode';
  var URL_PARAM   = 'personalised_debug';

  // ----- Determine initial enabled state -----

  var _enabled = false;

  // 1. Check sessionStorage (catches reloads after URL-param activation).
  try {
    if (sessionStorage.getItem(SESSION_KEY) === 'true') {
      _enabled = true;
    }
  } catch (e) { /* sessionStorage unavailable (private mode, Tapcart, etc.) */ }

  // 2. Check URL param — also persists to sessionStorage for the session.
  try {
    var _params = new URLSearchParams(window.location.search);
    if (_params.get(URL_PARAM) === 'true') {
      _enabled = true;
      try { sessionStorage.setItem(SESSION_KEY, 'true'); } catch (e) {}
    }
  } catch (e) { /* URLSearchParams unavailable */ }

  // ----- Helper: build console args array -----

  function _args(level, msg, rest) {
    var out = [PREFIX + ' [' + level + ']', msg];
    for (var i = 0; i < rest.length; i++) out.push(rest[i]);
    return out;
  }

  // ----- Controller object -----

  window.personalised.debug = {

    /**
     * Returns whether debug mode is currently active.
     * @returns {boolean}
     */
    isEnabled: function () { return _enabled; },

    /**
     * Log a debug-level message. Silent in production.
     * @param {string} msg
     * @param {...*} data
     */
    debug: function (msg) {
      if (!_enabled) return;
      var rest = Array.prototype.slice.call(arguments, 1);
      console.debug.apply(console, _args('debug', msg, rest));
    },

    /**
     * Log an info-level message. Silent in production.
     * @param {string} msg
     * @param {...*} data
     */
    info: function (msg) {
      if (!_enabled) return;
      var rest = Array.prototype.slice.call(arguments, 1);
      console.info.apply(console, _args('info', msg, rest));
    },

    /**
     * Log a warning. Always visible regardless of debug mode.
     * @param {string} msg
     * @param {...*} data
     */
    warn: function (msg) {
      var rest = Array.prototype.slice.call(arguments, 1);
      console.warn.apply(console, _args('warn', msg, rest));
    },

    /**
     * Log an error. Always visible regardless of debug mode.
     * @param {string} msg
     * @param {...*} data
     */
    error: function (msg) {
      var rest = Array.prototype.slice.call(arguments, 1);
      console.error.apply(console, _args('error', msg, rest));
    },

    /**
     * Start a named timer. Silent in production.
     * @param {string} label
     */
    time: function (label) {
      if (!_enabled) return;
      try { console.time(PREFIX + ' ' + label); } catch (e) {}
    },

    /**
     * End a named timer and log elapsed time. Silent in production.
     * @param {string} label
     */
    timeEnd: function (label) {
      if (!_enabled) return;
      try { console.timeEnd(PREFIX + ' ' + label); } catch (e) {}
    },

    /**
     * Trace a named flow event with optional structured data. Silent in production.
     * @param {string} eventName  — structured name, e.g. 'review:modeDetected'
     * @param {Object} [data]     — optional metadata object
     */
    trace: function (eventName, data) {
      if (!_enabled) return;
      var ts = Date.now();
      var args = [PREFIX + ' [trace] ' + eventName, { ts: ts }];
      if (data !== undefined) args.push(data);
      console.debug.apply(console, args);
    }
  };

})();
